import {Source} from "@/domain/type/source.type.ts";
import SynchroApi from "@/api/synchro.api.ts";
import {RequestEntityState, RequestEntityStatus} from "@/domain/type/synchro.api.type.ts";
import {CreateStatus} from "@/domain/type/create.type.ts";
import CompanyApi from "@/api/company.api.ts";
import {SynchroType} from "@/domain/type/synchro.type.ts";

const DELAY = {short: 1500, medium: 2000, long: 3000} as const;
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
const openAfter = async (url: string, ms: number) => {
  await sleep(ms);
  window.open(url, "_self");
};

type SynchroEntitySummary = { uuid: string; status: RequestEntityStatus; state?: RequestEntityState };
type RequestResponseLite = { data: { entities: SynchroEntitySummary[] } };
type MatchingEntity = { label: string; val: string | number };
type SynchroEntityResponse = { data: { matchingEntities: MatchingEntity[][] } };

export default (
  apis: { CompanyApi: typeof CompanyApi; SynchroApi: typeof SynchroApi },
  ports: {
    getCurrentTarget: () => Source;
    getMainTarget: () => Source | undefined;
    setStatus: (s: CreateStatus) => void;
    setManualForm: (v: boolean) => void;
  }
) => {
  const {CompanyApi, SynchroApi} = apis;
  const {getCurrentTarget, getMainTarget, setStatus, setManualForm} = ports;

  function buildStatusMessage(status: CreateStatus, target: Source): string {
    switch (status) {
      case CreateStatus.checkIfExistOnTarget:
        return `Recherche dans ${target}...`;
      case CreateStatus.checkIfExistOnSirene:
        return `Recherche dans le répertoire sirène...`;
      case CreateStatus.notExisting:
        return "Cette entreprise n'existe pas dans le répertoire sirène";
      case CreateStatus.anonymized:
        return "";
      case CreateStatus.alreadyCreated:
        return "Cette entreprise existe déjà dans " + target + " Redirection en cours...";
      case CreateStatus.analyzing:
        return `Analyse des données ${target} en cours...`;
      case CreateStatus.creating:
        return `Création de l'entreprise dans ${target} en cours...`;
      case CreateStatus.redirection:
        return "Redirection vers l'entreprise créée dans " + target;
      case CreateStatus.error:
        return "Serveur indisponible veuillez réessayer";
      default:
        return "";
    }
  }

  async function handleEntityLink(entityResponse: SynchroEntityResponse): Promise<void> {
    const groups = entityResponse.data.matchingEntities ?? [];
    let link: string | null = null;

    for (const group of groups) {
      for (const matchingEntity of group) {
        if (matchingEntity.label === "Lien" && getMainTarget() === getCurrentTarget()) {
          link = matchingEntity.val.toString();
          break;
        }
      }
      if (link) break;
    }

    if (link) {
      setStatus(CreateStatus.redirection);
      await openAfter(link, DELAY.short);
    }
  }

  async function processEntity(entity: SynchroEntitySummary): Promise<void> {
    const isSynchronized =
      entity.status === RequestEntityStatus.SYNCHRONIZED || entity.state === RequestEntityState.MATCH;

    if (isSynchronized) {
      setStatus(CreateStatus.alreadyCreated);
      await sleep(DELAY.medium);
    } else if (entity.status === RequestEntityStatus.TO_SYNC) {
      setStatus(CreateStatus.creating);
      await SynchroApi.applyEntity(entity.uuid);
      await sleep(DELAY.medium);
    }

    if (entity.status === RequestEntityStatus.TO_SYNC || entity.status === RequestEntityStatus.SYNCHRONIZED) {
      const entityResponse: SynchroEntityResponse = await SynchroApi.getEntity(entity.uuid);
      setStatus(CreateStatus.done);
      await handleEntityLink(entityResponse);
    }
  }

  async function createManually(siret: string, companyName: string): Promise<void> {
    const target = getCurrentTarget();
    if (!target) {
      throw new Error("Current target is not set");
    }

    setStatus(CreateStatus.analyzing);
    const createCompanyResponse = await CompanyApi.createCompany(siret, companyName, target);
    const location = (createCompanyResponse.headers as Record<string, unknown>)["location"] as string | undefined;
    if (location) {
      setStatus(CreateStatus.redirection);
      await openAfter(location, DELAY.long);
    }
    setStatus(CreateStatus.none);
  }

  async function createFromSiren(siret: string): Promise<void> {
    try {
      const target = getCurrentTarget();

      // 1) Existence dans la cible
      setStatus(CreateStatus.checkIfExistOnTarget);
      const existResponse = await CompanyApi.exists(siret, target);
      if (existResponse.data.exists) {
        setStatus(CreateStatus.alreadyCreated);
        const company = await CompanyApi.getCompany(siret, target);
        if (!company) {
          console.error("Company not found in target");
          return;
        }
        await openAfter(company.link, DELAY.medium);
        return;
      }

      // 2) Existence dans SIRENE
      setStatus(CreateStatus.checkIfExistOnSirene);
      await sleep(DELAY.medium);

      const existsSirene = await CompanyApi.exists(siret, Source.SIREN);
      if (!existsSirene.data.exists) {
        setStatus(CreateStatus.notExisting);
        return;
      }

      const sireneCompany = await CompanyApi.getCompany(siret, Source.SIREN);
      if (!sireneCompany?.name) {
        setManualForm(true);
        setStatus(CreateStatus.anonymized);
        return;
      }

      // 3) Demande de synchro
      setStatus(CreateStatus.analyzing);
      const demandResponse = await SynchroApi.demand(
        Source.SIREN,
        target,
        SynchroType.COMPANIES,
        siret
      );
      if (demandResponse.data.error && demandResponse.data.error === "Not diffusable Establishment") {
        setManualForm(true);
        setStatus(CreateStatus.anonymized)
        return
      }

      if (demandResponse.data.requestUuid) {
        const requestResponse: RequestResponseLite = await SynchroApi.getRequest(demandResponse.data.requestUuid);

        if (requestResponse.data.entities.length === 0) {
          setStatus(CreateStatus.noDataFound);
          return;
        }

        // 4) Traitement de la première entité
        await processEntity(requestResponse.data.entities[0]);
      }

    } catch (e) {
      console.error(e);
      setStatus(CreateStatus.error);
    }
  }

  return {createFromSiren, createManually, buildStatusMessage};
}
